import { Dialog, Select } from "@mantine/core";
import { useClickOutside, useDisclosure } from "@mantine/hooks";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { api } from "#app/http/api";
import { showErrorToast, showSuccessToast } from "#app/notifications";
import { colors } from "#app/theme/v1/colors";
import { useResetFileHashes } from "#modules/admin/view/useResetFileHashes";
import { useCompany } from "#modules/company/view/useCompany";
import { useReplayOnboarding } from "#modules/company/view/useReplayOnboarding";
import { useUpdateCompanyModel } from "#modules/company/view/useUpdateCompanyModel";
import { useAllModels } from "#modules/models/view/useAllModels";
import { useMyModel } from "#modules/models/view/useModel";
import { useMyModelVersion } from "#modules/models/view/useModelVersion";
import { selectInputOnFocus } from "#shared/helpers/selectInputOnFocus";
import { Icon } from "#shared/icons/Icon";
import { ROUTES } from "#shared/routes";
import { Alert } from "#shared/view/components/Alert";
import { Space } from "#shared/view/components/Space";
import { Button } from "#shared/view/designSystem/Button";
import { FileButton } from "#shared/view/designSystem/FileButton";

export const DevtoolsMenu = () => {
  const { t } = useTranslation();
  const { company } = useCompany();
  const { model } = useMyModel();
  const { allModels } = useAllModels();
  const { updateCompanyModel } = useUpdateCompanyModel();
  const { resetFileHashes, isPending: isResetting } = useResetFileHashes();
  const [opened, { close, open }] = useDisclosure(false);
  const navigate = useNavigate();

  const { replayOnboarding, isPending: isReplayingOnboarding } = useReplayOnboarding({
    onSuccess: () => navigate(ROUTES.v2),
  });

  const { modelVersion, isFetching: isFetchingModelVersion } = useMyModelVersion({
    enabled: opened,
  });
  const ref = useClickOutside(() => close());

  const { mutate: importConfig, isPending: isImporting } = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.companySettings.importConfig.mutation({
        body: formData as unknown as { file: File },
      });
      if (response.status !== 200) {
        const body = response.body as { message?: string };
        throw new Error(body.message ?? "Import failed");
      }
      return response.body;
    },
    onSuccess: () => {
      showSuccessToast({ title: "Configuration importée avec succès" });
      setTimeout(() => window.location.reload(), 1000);
    },
    onError: (err: Error) => showErrorToast({ title: err.message }),
  });

  const selectOptions = allModels
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((m) => ({ value: m.id, label: m.name }));

  const modelVersionLabel = isFetchingModelVersion
    ? "Version : chargement…"
    : `Version : ${modelVersion ?? "inconnue"}`;

  if (!company) {
    return <Alert>No company found</Alert>;
  }

  return (
    <>
      <Button variant="subtle" onClick={open} color="teal" aria-label={t("developer.openDevtools")}>
        <Icon name="code" />
      </Button>
      <Dialog
        ref={ref}
        opened={opened}
        position={{ bottom: 60, left: 20 }}
        withCloseButton
        onClose={close}
        bg={colors.teal[0]}
      >
        <Select
          label="Modèle"
          description={modelVersionLabel}
          data={selectOptions}
          value={model?.id}
          searchable
          onFocus={selectInputOnFocus}
          onChange={(value) => {
            if (!value) {
              return null;
            }
            updateCompanyModel({
              companySettingsId: company.companySettingsId,
              modelId: value,
            });
          }}
        />
        <Space h="sm" />
        <Button onClick={() => resetFileHashes()} loading={isResetting} variant="outline">
          Supprimer le cache
        </Button>
        <Space h="sm" />
        <Button
          onClick={() => replayOnboarding()}
          loading={isReplayingOnboarding}
          variant="outline"
        >
          Rejouer l'onboarding
        </Button>
        <Space h="sm" />
        <FileButton
          accept=".json,application/json"
          onChange={(file) => {
            if (file) {
              importConfig(file);
            }
          }}
        >
          {(props) => (
            <Button {...props} variant="outline" loading={isImporting}>
              Importer une configuration
            </Button>
          )}
        </FileButton>
      </Dialog>
    </>
  );
};
